// src/pages/RegularPredictions.js

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CheckIcon } from "@heroicons/react/20/solid";
import API from "../api";
import PredictionSkeleton from "../components/PredictionSkeleton";
import Card from "../components/ui/Card";
import MatchRow from "../components/ui/MatchRow";
import ScorePicker, { QuickScoreChips } from "../components/ui/ScorePicker";
import { getTeamLogo } from "../getTeamLogo";
import FixtureCloseCountdown from "../components/FixtureCloseCountdown";
import MatchInsight from "../components/MatchInsight";
import { isFixturePredictionOpen, getFixturePredictionDeadline } from "../utils/fixtureDeadline";
import { WC_ACTIVE_SEASON, formatWorldCupStageLabel } from "../constants/worldCup";
import { CURRENT_SEASON } from "../constants/season";

const CACHE_VER = "v8";
const DRAFT_VER = "v1";

const LOGO_DIRS = {
  premier_league: "england__premier_league",
  laliga: "spain__laliga",
  bundesliga: "germany__bundesliga",
  serie_a: "italy__serie_a",
};
const LOGO_COMPETITIONS = ["premier_league", "laliga", "bundesliga", "serie_a", "world_cup"];

const normalizeCrestKey = (value) =>
  (value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, "and")
    .replace(/\b(fc|afc|cf|sc|ud|cd|club|de|del|la|el|the)\b/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const TEAM_ALIASES = [
  { re: /\bmanchester united\b|\bman united\b|\bman utd\b/g, value: "manchester united" },
  { re: /\bmanchester city\b|\bman city\b/g, value: "manchester city" },
  { re: /\btottenham hotspur\b|\bspurs\b|\btottenham\b/g, value: "tottenham hotspur" },
  { re: /\bwolverhampton wanderers\b|\bwolves\b/g, value: "wolverhampton wanderers" },
  { re: /\bbrighton hove albion\b|\bbrighton\b/g, value: "brighton hove albion" },
  { re: /\bwest ham united\b|\bwest ham\b/g, value: "west ham united" },
  { re: /\bnewcastle united\b|\bnewcastle\b/g, value: "newcastle united" },
  { re: /\bcrystal palace\b/g, value: "crystal palace" },
  { re: /\bnottingham forest\b/g, value: "nottingham forest" },
  { re: /\baston villa\b/g, value: "aston villa" },
  { re: /\bliverpool\b/g, value: "liverpool" },
  { re: /\bchelsea\b/g, value: "chelsea" },
  { re: /\beverton\b/g, value: "everton" },
  { re: /\bbournemouth\b/g, value: "bournemouth" },
  { re: /\bfulham\b/g, value: "fulham" },
  { re: /\bleeds united\b|\bleeds\b/g, value: "leeds united" },
  { re: /\bburnley\b/g, value: "burnley" },
  { re: /\bbrentford\b/g, value: "brentford" },
  { re: /\barsenal\b/g, value: "arsenal" },
  { re: /\bsunderland\b/g, value: "sunderland" },
];

function normalizeTeam(name) {
  let n = (name || "").toLowerCase();
  n = n.replace(/&/g, "and");
  n = n.replace(/\b(fc|afc|cf|sc|ud|cd|club|deportivo|athletic|football club)\b/g, "");
  n = n.replace(/[^a-z0-9]+/g, " ").replace(/\s+/g, " ").trim();
  n = n.replace(/\band\b/g, "").replace(/\s+/g, " ").trim();
  for (const a of TEAM_ALIASES) {
    a.re.lastIndex = 0;
    if (a.re.test(n)) return a.value;
  }
  return n;
}

const matchDayKey = (m) => {
  if (typeof m.match_date === "string") return m.match_date.slice(0, 10);
  const d = new Date(m.match_date);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
};

const matchKey = (m) =>
  `${m.competition || ""}|${matchDayKey(m)}|${normalizeTeam(m.home_team)}|${normalizeTeam(m.away_team)}`;

const isCompleted = (m) => {
  const st = (m.status || "").toLowerCase();
  const hasScores =
    m.home_score !== null && m.home_score !== undefined && m.away_score !== null && m.away_score !== undefined;
  return st === "finished" || hasScores;
};

const isPredictableOpen = (m) => {
  const st = (m.status || "").toLowerCase();
  if (isCompleted(m)) return false;
  if (!(st === "scheduled" || st === "postponed" || st === "live")) return false;
  return isFixturePredictionOpen(m);
};

function dedupeByMatch(list, hasPrediction) {
  const byKey = new Map();
  const byApi = new Set();

  for (const m of list) {
    const normKey = matchKey(m);
    const apiKey = m.api_fixture_id ? `api:${m.api_fixture_id}` : null;
    if (apiKey && byApi.has(apiKey)) continue;

    const existing = byKey.get(normKey);
    if (!existing) {
      byKey.set(normKey, m);
      if (apiKey) byApi.add(apiKey);
      continue;
    }

    // Prefer the non-completed row when duplicates exist.
    if (isCompleted(existing) && !isCompleted(m)) {
      byKey.set(normKey, m);
      if (apiKey) byApi.add(apiKey);
      continue;
    }
    if (!isCompleted(existing) && isCompleted(m)) {
      if (apiKey) byApi.add(apiKey);
      continue;
    }

    const existingPred = hasPrediction(existing.id);
    const currentPred = hasPrediction(m.id);
    if (!existingPred && currentPred) {
      byKey.set(normKey, m);
      if (apiKey) byApi.add(apiKey);
      continue;
    }
    if (existingPred && currentPred) {
      if (apiKey) byApi.add(apiKey);
      continue;
    }

    if (!existing.api_fixture_id && m.api_fixture_id) {
      byKey.set(normKey, m);
      byApi.add(`api:${m.api_fixture_id}`);
      continue;
    }
    if (existing.last_sync_at && m.last_sync_at && new Date(m.last_sync_at) > new Date(existing.last_sync_at)) {
      byKey.set(normKey, m);
      if (apiKey) byApi.add(apiKey);
    }
  }

  return Array.from(byKey.values());
}

function dayLabel(date) {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "Date TBC";
  const today = new Date();
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOf(d) - startOf(today)) / 86400000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Tomorrow";
  return d.toLocaleDateString([], { weekday: "long", day: "numeric", month: "short" });
}

function kickoffTime(date) {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "TBC";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

const draftKey = (competition) => `p90_drafts_${DRAFT_VER}_${competition}`;

function readDrafts(competition) {
  try {
    const parsed = JSON.parse(localStorage.getItem(draftKey(competition)) || "{}");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (_) {
    return {};
  }
}

const editable = (m) => !m.postponed;

const filled = (v) => v !== "" && v !== null && v !== undefined;

const RegularPredictions = ({ competition, wcStageFilter = "", refreshKey, onRefresh, onEditingStateChange }) => {
  const [searchParams] = useSearchParams();
  const focusFixtureId = searchParams.get("fixture");
  const scrolledFixture = useRef(null);
  const [fixtures, setFixtures] = useState([]);
  // Server-confirmed picks: { [fixtureId]: { home_score, away_score, points_awarded } }
  const [predictions, setPredictions] = useState({});
  // Unsaved edits: { [fixtureId]: { home, away } }
  const [drafts, setDrafts] = useState(() => readDrafts(competition));
  const [saveState, setSaveState] = useState({ status: "idle", message: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [crestMap, setCrestMap] = useState({});

  const cacheKey = `predictions_${CACHE_VER}_${competition}_${wcStageFilter || "all"}`;
  const { data: insight } = useQuery({
    queryKey: ["matchInsights", competition, CURRENT_SEASON],
    queryFn: () =>
      API.get("/predictions/match-insights", {
        params: { competition, season: CURRENT_SEASON },
      }).then((res) => res.data),
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const cachedData = sessionStorage.getItem(cacheKey);
        const cacheTime = sessionStorage.getItem(`${cacheKey}_time`);
        const isCacheValid = cacheTime && Date.now() - parseInt(cacheTime, 10) < 300000;

        if (!refreshKey && cachedData && isCacheValid) {
          const parsed = JSON.parse(cachedData);
          if (parsed?.competition === competition) {
            setFixtures(parsed.fixtures || []);
            setPredictions(parsed.predictions || {});
            setLoading(false);
            return;
          }
        }

        const fixtureParams = { competition };
        if (competition === "world_cup") {
          fixtureParams.tournament_season = WC_ACTIVE_SEASON;
          if (wcStageFilter) fixtureParams.stage = wcStageFilter;
        } else {
          fixtureParams.season = CURRENT_SEASON;
        }
        const { data } = await API.get("/gameweeks/all-fixtures", { params: fixtureParams });
        let fixturesData = Array.isArray(data) ? data : [];

        // Safety: ensure competition is correct if API returns mixed data
        if (competition !== "ucl") {
          fixturesData = fixturesData.filter((m) => !m.competition || m.competition === competition);
        }

        const { data: preds } = await API.get(
          `/predictions/my-predictions?competition=${encodeURIComponent(competition)}`
        );
        const rawPredictions = Array.isArray(preds) ? preds : [];

        const formatted = {};
        fixturesData.forEach((match) => {
          const p = rawPredictions.find((x) => x.fixture_id === match.id);
          formatted[match.id] = {
            home_score: p?.predicted_home_score?.toString() || "",
            away_score: p?.predicted_away_score?.toString() || "",
            points_awarded:
              p?.points_awarded === null || p?.points_awarded === undefined ? null : Number(p.points_awarded),
          };
        });

        setFixtures(fixturesData);
        setPredictions(formatted);

        sessionStorage.setItem(cacheKey, JSON.stringify({ competition, fixtures: fixturesData, predictions: formatted }));
        sessionStorage.setItem(`${cacheKey}_time`, Date.now().toString());
      } catch (err) {
        console.error("❌ Error fetching data:", err);
        setError(err?.response?.data?.message || "Failed to load fixtures/predictions.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competition, wcStageFilter, refreshKey]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (competition === "ucl" || competition === "wildcard") {
          if (!cancelled) setCrestMap({});
          return;
        }
        if (competition === "world_cup") {
          const map = {};
          fixtures.forEach((f) => {
            if (f.home_team && f.home_crest_url) map[normalizeCrestKey(f.home_team)] = f.home_crest_url;
            if (f.away_team && f.away_crest_url) map[normalizeCrestKey(f.away_team)] = f.away_crest_url;
          });
          if (!cancelled) setCrestMap(map);
          return;
        }
        const { data } = await API.get("/admin/live-league-table", { params: { competition } });
        const map = {};
        (Array.isArray(data) ? data : []).forEach((row) => {
          if (!row?.team_name || !row?.crest_url) return;
          map[normalizeCrestKey(row.team_name)] = row.crest_url;
        });
        if (!cancelled) setCrestMap(map);
      } catch (_) {
        if (!cancelled) setCrestMap({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [competition, fixtures]);

  useEffect(() => {
    try {
      if (Object.keys(drafts).length === 0) localStorage.removeItem(draftKey(competition));
      else localStorage.setItem(draftKey(competition), JSON.stringify(drafts));
    } catch (_) {}
  }, [drafts, competition]);

  useEffect(() => {
    if (saveState.status !== "saved") return;
    const id = setTimeout(() => setSaveState({ status: "idle", message: "" }), 4000);
    return () => clearTimeout(id);
  }, [saveState.status]);

  const savedPick = useCallback(
    (id) => ({
      home: predictions[id]?.home_score ?? "",
      away: predictions[id]?.away_score ?? "",
    }),
    [predictions]
  );

  const currentPick = useCallback((id) => drafts[id] || savedPick(id), [drafts, savedPick]);

  const upcomingFixtures = useMemo(() => {
    const hasPrediction = (id) => {
      const p = predictions[id];
      return !!p && (p.home_score !== "" || p.away_score !== "");
    };
    return dedupeByMatch([...fixtures], hasPrediction)
      .filter(isPredictableOpen)
      .sort((a, b) => new Date(a.match_date) - new Date(b.match_date));
  }, [fixtures, predictions]);

  useEffect(() => {
    if (loading || !focusFixtureId || scrolledFixture.current === focusFixtureId) return;
    const el = document.getElementById(`fixture-${focusFixtureId}`);
    if (!el) return;
    scrolledFixture.current = focusFixtureId;
    el.scrollIntoView({ block: "center" });
  }, [loading, focusFixtureId, upcomingFixtures]);

  // Drop drafts for fixtures that have locked or disappeared.
  useEffect(() => {
    if (loading) return;
    const openIds = new Set(upcomingFixtures.map((f) => String(f.id)));
    setDrafts((prev) => {
      const next = {};
      let pruned = false;
      Object.entries(prev).forEach(([id, v]) => {
        if (openIds.has(String(id))) next[id] = v;
        else pruned = true;
      });
      return pruned ? next : prev;
    });
  }, [loading, upcomingFixtures]);

  const summary = useMemo(() => {
    let picked = 0;
    let changed = 0;
    let incomplete = 0;
    upcomingFixtures.forEach((m) => {
      const cur = currentPick(m.id);
      const saved = savedPick(m.id);
      const complete = filled(cur.home) && filled(cur.away);
      const isChanged = cur.home !== saved.home || cur.away !== saved.away;
      if (complete) picked += 1;
      if (isChanged && complete && editable(m)) changed += 1;
      if (isChanged && !complete && (filled(cur.home) || filled(cur.away))) incomplete += 1;
    });
    return { open: upcomingFixtures.length, picked, changed, incomplete };
  }, [upcomingFixtures, currentPick, savedPick]);

  useEffect(() => {
    if (!onEditingStateChange) return;
    const nextDeadline =
      upcomingFixtures
        .map((f) => getFixturePredictionDeadline(f))
        .filter((d) => d && !Number.isNaN(d.getTime()))
        .sort((a, b) => a - b)[0] ?? null;
    onEditingStateChange({
      windowState: upcomingFixtures.length > 0 ? "open" : "closed",
      deadline: nextDeadline,
      openCount: summary.open,
      remainingOpenCount: Math.max(0, summary.open - summary.picked),
      unsavedCount: summary.changed,
    });
  }, [upcomingFixtures, summary, onEditingStateChange]);

  const setPick = (id, value) => {
    const saved = savedPick(id);
    setSaveState((s) => (s.status === "saving" ? s : { status: "idle", message: "" }));
    setDrafts((prev) => {
      const next = { ...prev };
      if (value.home === saved.home && value.away === saved.away) delete next[id];
      else next[id] = value;
      return next;
    });
  };

  const handleSave = async () => {
    const changedComplete = upcomingFixtures.filter((m) => {
      const cur = currentPick(m.id);
      const saved = savedPick(m.id);
      return (
        editable(m) &&
        filled(cur.home) &&
        filled(cur.away) &&
        (cur.home !== saved.home || cur.away !== saved.away)
      );
    });
    const stillOpen = changedComplete.filter(isPredictableOpen);
    const lockedCount = changedComplete.length - stillOpen.length;

    if (stillOpen.length === 0) {
      setSaveState({
        status: "error",
        message: lockedCount ? "Those matches locked before you saved." : "Nothing to save yet.",
      });
      return;
    }

    const payload = stillOpen.map((m) => {
      const cur = currentPick(m.id);
      return { fixture_id: m.id, home_score: Number(cur.home), away_score: Number(cur.away) };
    });

    setSaveState({ status: "saving", message: "" });
    try {
      await API.post("/predictions/submit", { predictions: payload });

      try {
        sessionStorage.removeItem(cacheKey);
        sessionStorage.removeItem(`${cacheKey}_time`);
      } catch (_) {}

      setPredictions((prev) => {
        const next = { ...prev };
        payload.forEach((p) => {
          next[p.fixture_id] = {
            ...next[p.fixture_id],
            home_score: String(p.home_score),
            away_score: String(p.away_score),
          };
        });
        return next;
      });
      setDrafts((prev) => {
        const next = { ...prev };
        payload.forEach((p) => delete next[p.fixture_id]);
        return next;
      });

      const savedLabel = `${payload.length} ${payload.length === 1 ? "pick" : "picks"} saved`;
      setSaveState({
        status: "saved",
        message: lockedCount ? `${savedLabel} · ${lockedCount} locked before saving` : savedLabel,
      });
      onRefresh?.();
    } catch (err) {
      console.error("❌ Error submitting predictions:", err);
      setSaveState({
        status: "error",
        message: err?.response?.data?.message || err.message || "Couldn't save. Your picks are kept on this device.",
      });
    }
  };

  if (loading) return <PredictionSkeleton />;

  if (error) {
    return (
      <Card>
        <p className="text-center text-red-700 py-2">{error}</p>
      </Card>
    );
  }

  if (upcomingFixtures.length === 0) {
    return (
      <Card>
        <div className="text-center py-4">
          <p className="font-semibold text-ink">No matches open for picks</p>
          <p className="mt-1 text-meta text-muted">
            New fixtures open here once they're scheduled. Results and past picks are on Fixtures.
          </p>
          <Link
            to="/fixtures"
            className="mt-4 inline-flex items-center h-10 px-4 rounded-lg border border-line bg-white text-sm font-semibold text-ink hover:border-gray-400"
          >
            Go to Fixtures
          </Link>
        </div>
      </Card>
    );
  }

  const leagueDir = LOGO_DIRS[competition] || LOGO_DIRS.premier_league;
  const showLogos = LOGO_COMPETITIONS.includes(competition);
  const logoFor = (name, crestUrl) => {
    if (!showLogos) return null;
    if (competition === "world_cup") return crestUrl || crestMap[normalizeCrestKey(name)] || null;
    return getTeamLogo(name, leagueDir) || crestMap[normalizeCrestKey(name)] || null;
  };

  const groups = [];
  upcomingFixtures.forEach((m) => {
    const label = dayLabel(m.match_date);
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.items.push(m);
    else groups.push({ label, items: [m] });
  });

  const saving = saveState.status === "saving";
  let barNote;
  if (saving) barNote = "Saving…";
  else if (saveState.status === "error") barNote = saveState.message;
  else if (saveState.status === "saved") barNote = saveState.message;
  else if (summary.changed > 0)
    barNote = [
      `${summary.changed} unsaved`,
      summary.incomplete > 0 ? `${summary.incomplete} need${summary.incomplete === 1 ? "s" : ""} both scores` : null,
    ]
      .filter(Boolean)
      .join(" · ");
  else if (summary.incomplete > 0)
    barNote = `${summary.incomplete} ${summary.incomplete === 1 ? "pick needs" : "picks need"} both scores`;
  else if (summary.picked === 0) barNote = "Tap a quick score or use + and −";
  else if (summary.picked < summary.open) barNote = `Saved · ${summary.open - summary.picked} still to pick`;
  else barNote = "All picks saved";

  return (
    <div className="pb-24 md:pb-28">
      <div className="space-y-6">
        {groups.map((group) => (
          <section key={group.label} aria-label={group.label}>
            <h2 className="mb-2 px-1 text-meta font-semibold text-gray-700">{group.label}</h2>
            <div className="space-y-3">
              {group.items.map((match) => {
                const canEdit = editable(match);
                const cur = currentPick(match.id);
                const saved = savedPick(match.id);
                const isChanged = cur.home !== saved.home || cur.away !== saved.away;
                const complete = filled(cur.home) && filled(cur.away);
                const stageLabel =
                  competition === "world_cup" ? formatWorldCupStageLabel(match.stage, match.matchday) : null;

                let pickState;
                if (isChanged) {
                  pickState = <span className="font-semibold text-brand">{complete ? "Unsaved" : "Incomplete"}</span>;
                } else if (complete) {
                  pickState = (
                    <span className="inline-flex items-center gap-0.5 text-gray-700">
                      <CheckIcon className="w-4 h-4" aria-hidden /> Picked
                    </span>
                  );
                } else {
                  pickState = <span>Not picked</span>;
                }

                const focused = String(match.id) === String(focusFixtureId);
                return (
                  <MatchRow
                    key={match.id}
                    competition={competition}
                    id={`fixture-${match.id}`}
                    className={focused ? "scroll-mt-24 ring-2 ring-inset ring-brand" : ""}
                    home={{ name: match.home_team, logo: logoFor(match.home_team, match.home_crest_url) }}
                    away={{ name: match.away_team, logo: logoFor(match.away_team, match.away_crest_url) }}
                    meta={
                      <span className="inline-flex items-center gap-1.5">
                        <span className="tabular-nums text-gray-700 font-medium">{kickoffTime(match.match_date)}</span>
                        {stageLabel && <span>· {stageLabel}</span>}
                        <span aria-hidden>·</span>
                        {pickState}
                      </span>
                    }
                    metaRight={
                      match.postponed ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[12px] leading-5 font-semibold border bg-gray-100 text-gray-700 border-gray-200">
                          Postponed
                        </span>
                      ) : (
                        <FixtureCloseCountdown fixture={match} />
                      )
                    }
                    center={
                      canEdit ? (
                        <ScorePicker
                          homeName={match.home_team}
                          awayName={match.away_team}
                          value={cur}
                          onChange={(v) => setPick(match.id, v)}
                        />
                      ) : (
                        <span className="text-score font-bold tabular-nums text-ink">
                          {complete ? `${cur.home}–${cur.away}` : "–"}
                        </span>
                      )
                    }
                    footer={
                      <div className="space-y-2">
                        {canEdit ? <QuickScoreChips value={cur} onPick={(v) => setPick(match.id, v)} /> : null}
                        <MatchInsight match={match} insight={insight} />
                      </div>
                    }
                  />
                );
              })}
            </div>
          </section>
        ))}
      </div>

      <div className="fixed inset-x-0 bottom-above-nav z-40 bg-white border-t border-line shadow-bar">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:grid lg:grid-cols-[minmax(0,1fr)_320px] xl:grid-cols-[minmax(0,1fr)_380px] lg:gap-6 xl:gap-8">
        <div className="py-2.5 flex items-center justify-between gap-3">
          <div className="min-w-0" aria-live="polite">
            <p className="text-[15px] font-semibold text-ink tabular-nums">
              {summary.picked} of {summary.open} picked
            </p>
            <p
              className={`text-[12px] truncate ${
                saveState.status === "error"
                  ? "text-red-700"
                  : saveState.status === "saved"
                    ? "text-green-700"
                    : "text-muted"
              }`}
            >
              {barNote}
            </p>
          </div>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || summary.changed === 0}
            className="shrink-0 h-11 px-5 rounded-lg bg-brand text-white text-[15px] font-semibold hover:bg-brand-hover disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            {saving && (
              <span aria-hidden className="inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            )}
            {saving
              ? "Saving"
              : summary.changed > 0
                ? `Save ${summary.changed}`
                : summary.picked > 0
                  ? "Saved"
                  : "Save"}
          </button>
        </div>
        </div>
      </div>
    </div>
  );
};

export default RegularPredictions;
